import React, { useEffect, useMemo, useState } from 'react';

// Fallback list — only used if /api/exam-types can't be reached (server auto-seeds these on first call)
import { FileEdit, Printer, X, BarChart3 } from "lucide-react";
const EXAM_FALLBACK = ['Mid Term 1', 'Quarterly', 'Mid Term 2', 'Half Yearly', 'Mid Term 3', 'Annual'];

// ── Parse className into grade + department ────────────────────────────────
// Examples:
//   "Class 3-A"              -> { grade: '3',  department: '' }
//   "Class 11 BioMaths-A"    -> { grade: '11', department: 'bio' }
//   "Class 12 ComputerScience-B" -> { grade: '12', department: 'computer' }
//   "UKG-A"                  -> { grade: 'UKG', department: '' }
function parseClassMeta(className) {
  if (!className) return {
    grade: '',
    department: ''
  };
  const dashIdx = className.lastIndexOf('-');
  const left = dashIdx !== -1 ? className.slice(0, dashIdx).trim() : className.trim();
  const m = left.match(/^Class\s+(\d+)/i);
  let grade = '';
  let rest = '';
  if (m) {
    grade = m[1];
    rest = left.slice(m[0].length).trim();
  } else if (/^(UKG|LKG)$/i.test(left)) {
    grade = left.toUpperCase();
  } else {
    grade = left;
  }
  let department = '';
  if (rest) {
    const clean = rest.toLowerCase().replace(/\s/g, '');
    if (clean.includes('bio')) department = 'bio';else if (clean.includes('computer') || clean.includes('cs')) department = 'computer';else if (clean.includes('art')) department = 'art';else if (clean.includes('commerce')) department = 'commerce';
  }
  return {
    grade,
    department
  };
}

// Responsive hook
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}
export default function Marksheet({
  className,
  API_BASE,
  onClose,
  staffId,
  staffData
}) {
  const isMobile = useIsMobile();
  const {
    grade,
    department
  } = useMemo(() => parseClassMeta(className), [className]);
  // Split "Class 1-A" -> classLabel "Class 1", section "A" (for the report card top row)
  const {
    classLabel,
    section
  } = useMemo(() => {
    if (!className) return {
      classLabel: '',
      section: ''
    };
    const dashIdx = className.lastIndexOf('-');
    if (dashIdx === -1) return {
      classLabel: className,
      section: ''
    };
    return {
      classLabel: className.slice(0, dashIdx).trim(),
      section: className.slice(dashIdx + 1).trim()
    };
  }, [className]);
  const styles = {
    container: {
      padding: isMobile ? 10 : 16,
      background: 'linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)',
      borderRadius: 12,
      boxShadow: '0 8px 30px rgba(15,23,42,0.06)'
    },
    header: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      flexWrap: isMobile ? 'nowrap' : 'wrap',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginBottom: 14,
      gap: isMobile ? 10 : 12
    },
    title: {
      fontSize: isMobile ? 16 : 20,
      fontWeight: 800,
      color: '#0f172a'
    },
    subtitle: {
      fontSize: 12,
      color: '#6b7280'
    },
    select: {
      padding: 10,
      borderRadius: 10,
      border: '1px solid #e6eefc',
      background: '#fff',
      minWidth: 200
    },
    reportHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid #eef2ff',
      paddingBottom: 12,
      marginBottom: 12
    },
    reportTable: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    tableRow: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1fr 1fr',
      gap: isMobile ? 4 : 8,
      padding: '8px 0',
      borderBottom: '1px solid #f1f5f9',
      alignItems: 'center'
    },
    tableHeaderCell: {
      fontWeight: 800,
      color: '#0f172a',
      fontSize: isMobile ? 12 : 14
    },
    tableCell: {
      color: '#0f172a',
      fontSize: isMobile ? 12 : 14
    },
    signatureRow: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      marginTop: 18,
      alignItems: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? 16 : 0
    },
    reportCard: {
      borderRadius: 14,
      padding: isMobile ? 12 : 20,
      background: 'linear-gradient(180deg,#ffffff 0%,#fbfdff 100%)',
      boxShadow: '0 18px 40px rgba(2,6,23,0.06)',
      border: '1px solid #eef6ff'
    },
    reportLogo: {
      width: 48,
      height: 48,
      borderRadius: 12,
      background: 'linear-gradient(135deg,#4f46e5,#06b6d4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontWeight: 900,
      fontSize: 16,
      flexShrink: 0
    },
    schoolName: {
      fontSize: isMobile ? 14 : 18,
      fontWeight: 900,
      background: 'linear-gradient(90deg,#0f172a,#334155)',
      WebkitBackgroundClip: 'text',
      color: 'transparent'
    },
    tableHeader: {
      padding: '8px 0',
      borderBottom: '2px solid #eef2ff',
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1fr 1fr',
      gap: isMobile ? 4 : 8,
      alignItems: 'center'
    },
    gradeBadge: {
      padding: isMobile ? '4px 8px' : '6px 12px',
      borderRadius: 20,
      fontWeight: 800,
      fontSize: isMobile ? 11 : 13
    },
    btnReport: {
      padding: isMobile ? '6px 10px' : '8px 12px',
      borderRadius: 10,
      border: 'none',
      background: 'linear-gradient(90deg,#06b6d4,#4f46e5)',
      color: '#fff',
      cursor: 'pointer',
      boxShadow: '0 8px 20px rgba(79,70,229,0.12)',
      fontWeight: 800,
      fontSize: isMobile ? 12 : 14
    },
    inputsRow: {
      display: 'flex',
      gap: 8,
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    gradeRow: {
      marginTop: 10,
      display: 'flex',
      alignItems: 'center',
      gap: 8
    },
    btnPrimary: {
      padding: isMobile ? '8px 12px' : '8px 14px',
      borderRadius: 10,
      border: 'none',
      background: 'linear-gradient(90deg,#4f46e5,#06b6d4)',
      color: '#fff',
      cursor: 'pointer',
      boxShadow: '0 6px 18px rgba(79,70,229,0.12)',
      fontSize: isMobile ? 13 : 14
    },
    btnSecondary: {
      padding: isMobile ? '6px 10px' : '8px 12px',
      borderRadius: 10,
      border: '1px solid #e6eefc',
      background: '#fff',
      cursor: 'pointer',
      fontSize: isMobile ? 12 : 14
    },
    saveAllBtn: {
      padding: '10px 16px',
      borderRadius: 10,
      fontSize: isMobile ? 13 : 15,
      marginTop: 16,
      height: 'auto'
    },
    studentCard: {
      padding: isMobile ? 10 : 12,
      borderBottom: '1px solid rgba(14,165,233,0.03)',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      cursor: 'pointer',
      transition: 'background .15s, transform .08s'
    },
    studentAvatar: {
      width: isMobile ? 36 : 44,
      height: isMobile ? 36 : 44,
      borderRadius: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 800,
      color: '#fff',
      flexShrink: 0
    },
    panel: {
      borderRadius: 12,
      border: '1px solid #eef2ff',
      padding: isMobile ? 10 : 14,
      background: '#ffffff'
    },
    card: {
      borderRadius: 12,
      padding: isMobile ? 10 : 14,
      background: '#fff',
      boxShadow: '0 6px 18px rgba(2,6,23,0.04)',
      border: '1px solid #f1f5f9'
    },
    input: {
      padding: 8,
      borderRadius: 8,
      border: '1px solid #eef2ff',
      width: isMobile ? 70 : 90,
      outline: 'none',
      fontSize: isMobile ? 13 : 14
    },
    tabsContainer: {
      display: 'flex',
      background: '#f1f5f9',
      padding: 4,
      borderRadius: 12,
      border: '1px solid #e2e8f0',
      gap: 4,
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      flexShrink: 1,
      minWidth: 0,
      maxWidth: isMobile ? '100%' : 'none',
      scrollbarWidth: 'none'
    },
    tabButton: {
      padding: isMobile ? '6px 10px' : '8px 16px',
      borderRadius: 10,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      fontWeight: 700,
      fontSize: isMobile ? 11 : 13,
      color: '#64748b',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap',
      fontFamily: "'DM Sans', sans-serif"
    },
    tabButtonActive: {
      background: '#fff',
      color: '#4f46e5',
      boxShadow: '0 4px 12px rgba(79,70,229,0.12)'
    },
    addExamBtn: {
      padding: isMobile ? '6px 10px' : '8px 14px',
      borderRadius: 10,
      border: '1px dashed #94a3b8',
      background: '#fff',
      cursor: 'pointer',
      fontWeight: 800,
      fontSize: isMobile ? 12 : 13,
      color: '#4f46e5',
      whiteSpace: 'nowrap',
      flexShrink: 0
    },
    modalOverlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(15,23,42,0.45)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 16
    },
    modalBox: {
      background: '#fff',
      borderRadius: 14,
      padding: 20,
      width: isMobile ? '100%' : 380,
      boxShadow: '0 20px 60px rgba(2,6,23,0.25)'
    },
    optionalChip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 8px',
      borderRadius: 999,
      background: '#eef2ff',
      color: '#4f46e5',
      fontSize: 11,
      fontWeight: 700
    },
    classTableTh: {
      border: '1px solid #e2e8f0',
      padding: isMobile ? '4px 6px' : '8px 10px',
      background: '#f8fafc',
      color: '#0f172a',
      fontWeight: 800,
      textAlign: 'center',
      whiteSpace: 'nowrap'
    },
    classTableTd: {
      border: '1px solid #e2e8f0',
      padding: isMobile ? '4px 6px' : '8px 10px',
      textAlign: 'center',
      color: '#0f172a'
    }
  };
  const AVATAR_PALETTE = [{
    bg: '#dbeafe',
    color: '#1e40af'
  }, {
    bg: '#dcfce7',
    color: '#166534'
  }, {
    bg: '#fef9c3',
    color: '#854d0e'
  }, {
    bg: '#ede9fe',
    color: '#5b21b6'
  }, {
    bg: '#fce7f3',
    color: '#9d174d'
  }];
  const avatarColor = i => AVATAR_PALETTE[i % AVATAR_PALETTE.length];

  // "S/O FatherName" for boys, "D/O FatherName" for girls — used next to the student's name
  const parentageLabel = student => {
    if (!student || !student.fatherName) return '';
    const g = (student.gender || '').toLowerCase();
    const rel = g.startsWith('f') ? 'D/O' : g.startsWith('m') ? 'S/O' : 'C/O';
    return `${rel} ${student.fatherName}`;
  };
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReportCard, setShowReportCard] = useState(false);
  const [savingSingleId, setSavingSingleId] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [subjectInputs, setSubjectInputs] = useState({});
  const [searchText, setSearchText] = useState('');

  // Exam types (dynamic, from DB) ------------------------------------------------
  const [examTypes, setExamTypes] = useState(EXAM_FALLBACK.map((n, i) => ({
    _id: n,
    name: n,
    order: i
  })));
  const [selectedExam, setSelectedExam] = useState('Mid Term 1');
  const [showAddExam, setShowAddExam] = useState(false);
  const [newExamName, setNewExamName] = useState('');
  const [addingExam, setAddingExam] = useState(false);

  // Subjects (main = mandatory, optional = dropdown) ------------------------------
  const [mainSubjects, setMainSubjects] = useState([]);
  const [optionalSubjectsAll, setOptionalSubjectsAll] = useState([]);
  const [activeOptionalSubjects, setActiveOptionalSubjects] = useState([]);
  const [addOptionalChoice, setAddOptionalChoice] = useState('');

  // Mobile: show student list or detail panel
  const [mobileView, setMobileView] = useState('list'); // 'list' | 'detail'

  // Class-wide "all students" report ------------------------------------------
  const [showClassReport, setShowClassReport] = useState(false);
  const [classReportLoading, setClassReportLoading] = useState(false);
  const [classMarksData, setClassMarksData] = useState({}); // { studentId: { subject: {marks,maxMarks} } }

  // ── Load exam types ────────────────────────────────────────────────────────
  useEffect(() => {
    const loadExams = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/exam-types`);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        const list = Array.isArray(data) && data.length ? data : EXAM_FALLBACK.map((n, i) => ({
          _id: n,
          name: n,
          order: i
        }));
        setExamTypes(list);
        setSelectedExam(prev => list.find(e => e.name === prev) ? prev : list[0]?.name || 'Mid Term 1');
      } catch (err) {
        console.error(err);
      }
    };
    loadExams();
  }, [API_BASE]);

  // ── Load main + optional subjects for this grade/department ────────────────
  useEffect(() => {
    if (!grade) return;
    const loadSubjects = async () => {
      try {
        const qs = department ? `grade=${encodeURIComponent(grade)}&department=${encodeURIComponent(department)}` : `grade=${encodeURIComponent(grade)}`;
        const res = await fetch(`${API_BASE}/api/subject-master?${qs}`);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        setMainSubjects(Array.isArray(data.main) ? data.main : []);
        setOptionalSubjectsAll(Array.isArray(data.optional) ? data.optional : []);
      } catch (err) {
        console.error(err);
        setMainSubjects(['Tamil', 'English', 'Maths', 'Science', 'Social Science', 'Value Education']);
        setOptionalSubjectsAll([]);
      }
    };
    loadSubjects();
  }, [API_BASE, grade, department]);

  // ── Load students for this class ────────────────────────────────────────────
  useEffect(() => {
    if (!className) return;
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/admissions/by-class/${encodeURIComponent(className)}`);
        const data = await res.json();
        setStudents(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, [className, API_BASE]);

  // ── Build the marks-entry inputs whenever the selected student/exam changes ─
  useEffect(() => {
    if (selectedStudent) {
      const allSubs = [...mainSubjects, ...activeOptionalSubjects];
      const inputs = {};
      allSubs.forEach(sub => {
        const found = reportData.find(x => x.subject === sub && x.exam === selectedExam);
        inputs[sub] = {
          marks: found?.entry?.marks ?? '',
          maxMarks: found?.entry?.maxMarks ?? 100
        };
      });
      setSubjectInputs(inputs);
    } else {
      setSubjectInputs({});
    }
  }, [selectedStudent, selectedExam, reportData, mainSubjects, activeOptionalSubjects]);
  const filteredStudents = students.filter(s => !searchText || (s.studentName || '').toLowerCase().includes(searchText.toLowerCase()) || String(s.rollNo || '').toLowerCase().includes(searchText.toLowerCase()));

  // ── Add a new exam type ──────────────────────────────────────────────────────
  const handleAddExam = async () => {
    const name = newExamName.trim();
    if (!name) return;
    setAddingExam(true);
    try {
      const res = await fetch(`${API_BASE}/api/exam-types`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name
        })
      });
      if (!res.ok) throw new Error('Failed');
      const created = await res.json();
      setExamTypes(prev => prev.find(e => e.name.toLowerCase() === created.name.toLowerCase()) ? prev : [...prev, created]);
      setSelectedExam(created.name);
      setNewExamName('');
      setShowAddExam(false);
    } catch (err) {
      console.error(err);
      alert('Exam add panna mudiyala. Try again.');
    } finally {
      setAddingExam(false);
    }
  };

  // ── Open a student's report / entry panel ────────────────────────────────────
  const openStudentReport = async student => {
    setSelectedStudent(student);
    setReportLoading(true);
    if (isMobile) setMobileView('detail');
    try {
      const res = await fetch(`${API_BASE}/api/marks/student/${student._id}?className=${encodeURIComponent(className)}`);
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      const studentMarks = Array.isArray(data) ? data : [];
      setReportData(studentMarks);
      // Any optional subject that already has a saved doc (any exam) should show as an active row
      const active = optionalSubjectsAll.filter(sub => studentMarks.some(d => d.subject === sub));
      setActiveOptionalSubjects(active);
    } catch (err) {
      console.error(err);
      setReportData([]);
      setActiveOptionalSubjects([]);
    } finally {
      setReportLoading(false);
    }
  };
  const addOptionalSubject = () => {
    if (!addOptionalChoice) return;
    setActiveOptionalSubjects(prev => prev.includes(addOptionalChoice) ? prev : [...prev, addOptionalChoice]);
    setSubjectInputs(prev => ({
      ...prev,
      [addOptionalChoice]: prev[addOptionalChoice] || {
        marks: '',
        maxMarks: 100
      }
    }));
    setAddOptionalChoice('');
  };
  const removeOptionalSubject = async sub => {
    if (!selectedStudent) return;
    const docMeta = reportData.find(d => d.subject === sub);
    if (docMeta && docMeta._id) {
      try {
        await fetch(`${API_BASE}/api/marks/${docMeta._id}/student/${selectedStudent._id}`, {
          method: 'DELETE'
        });
      } catch (err) {
        console.error(err);
      }
    }
    setActiveOptionalSubjects(prev => prev.filter(s => s !== sub));
    setSubjectInputs(prev => {
      const cp = {
        ...prev
      };
      delete cp[sub];
      return cp;
    });
  };
  const saveAllForSelectedStudent = async () => {
    if (!selectedStudent) return;
    const allSubs = [...mainSubjects, ...activeOptionalSubjects];
    if (allSubs.length === 0) return;
    setSavingSingleId(selectedStudent._id);
    try {
      for (const sub of allSubs) {
        const inp = subjectInputs[sub] || {
          marks: '',
          maxMarks: 100
        };
        const m = inp.marks === '' ? null : Number(inp.marks);
        const mm = Number(inp.maxMarks || 100);
        const payload = {
          className,
          subject: sub,
          staffId: staffId || '',
          academicYear: new Date().getFullYear().toString(),
          exam: selectedExam,
          entries: [{
            studentId: selectedStudent._id,
            marks: m,
            maxMarks: mm,
            grade: calcGrade(m, mm)
          }]
        };
        const res = await fetch(`${API_BASE}/api/marks/bulk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error(`Save failed for ${sub}`);
      }
      await openStudentReport(selectedStudent);
    } catch (err) {
      console.error(err);
      alert('Failed to save marks');
    } finally {
      setSavingSingleId(null);
    }
  };
  const closeStudentReport = () => {
    setSelectedStudent(null);
    setReportData([]);
    setActiveOptionalSubjects([]);
    setShowReportCard(false);
    if (isMobile) setMobileView('list');
  };

  // ── Load marks for ALL students in this class (for the "Class Report" table) ─
  const loadClassReport = async () => {
    setClassReportLoading(true);
    setShowClassReport(true);
    try {
      const res = await fetch(`${API_BASE}/api/marks?className=${encodeURIComponent(className)}&exam=${encodeURIComponent(selectedExam)}`);
      if (!res.ok) throw new Error('Failed');
      const docs = await res.json();
      const data = {};
      (Array.isArray(docs) ? docs : []).forEach(doc => {
        (doc.entries || []).forEach(entry => {
          const sid = String(entry.studentId);
          if (!data[sid]) data[sid] = {};
          data[sid][doc.subject] = {
            marks: entry.marks,
            maxMarks: entry.maxMarks
          };
        });
      });
      setClassMarksData(data);
    } catch (err) {
      console.error(err);
      setClassMarksData({});
    } finally {
      setClassReportLoading(false);
    }
  };
  const closeClassReport = () => {
    setShowClassReport(false);
    setClassMarksData({});
  };
  const gradeColor = grade => {
    if (!grade || grade === '—') return {
      bg: '#f1f5f9',
      color: '#0f172a'
    };
    if (grade === 'A+' || grade === 'A') return {
      bg: 'linear-gradient(90deg,#34d399,#06b6d4)',
      color: '#04261b'
    };
    if (grade === 'B') return {
      bg: 'linear-gradient(90deg,#60a5fa,#7c3aed)',
      color: '#07113a'
    };
    if (grade === 'C') return {
      bg: 'linear-gradient(90deg,#fbbf24,#fb7185)',
      color: '#3a1800'
    };
    return {
      bg: '#fca5a5',
      color: '#4a0303'
    };
  };
  const calcGrade = (marks, maxMarks) => {
    if (marks === null || marks === undefined) return '—';
    const pct = marks / (maxMarks || 100) * 100;
    if (pct >= 90) return 'A+';
    if (pct >= 80) return 'A';
    if (pct >= 70) return 'B';
    if (pct >= 60) return 'C';
    return 'D';
  };

  // Total & percentage — calculated ONLY from the main (mandatory) subjects
  const computeMainTotals = () => {
    let totalMarks = 0;
    let totalMax = 0;
    let anyEntered = false;
    mainSubjects.forEach(sub => {
      const entry = reportData.find(r => r.subject === sub && r.exam === selectedExam)?.entry || null;
      const inp = subjectInputs[sub] || {
        marks: entry?.marks ?? '',
        maxMarks: entry?.maxMarks ?? 100
      };
      const m = inp.marks === '' ? null : Number(inp.marks);
      const mm = Number(inp.maxMarks || 100);
      if (m !== null && !isNaN(m)) {
        anyEntered = true;
        totalMarks += m;
      }
      totalMax += isNaN(mm) ? 0 : mm;
    });
    const pct = anyEntered && totalMax > 0 ? totalMarks / totalMax * 100 : null;
    return {
      totalMarks,
      totalMax,
      pct
    };
  };

  // ─── ADD EXAM MODAL ─────────────────────────────────────────────────────────
  const AddExamModal = () => <div style={styles.modalOverlay} onClick={() => setShowAddExam(false)}>
      <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
        <div style={{
        fontWeight: 800,
        fontSize: 16,
        marginBottom: 12,
        color: '#0f172a'
      }}>Add New Exam</div>
        <input autoFocus placeholder="e.g. Revision Test" value={newExamName} onChange={e => setNewExamName(e.target.value)} onKeyDown={e => {
        if (e.key === 'Enter') handleAddExam();
      }} style={{
        width: '100%',
        padding: 10,
        borderRadius: 10,
        border: '1px solid #e6eefc',
        boxSizing: 'border-box',
        fontSize: 14,
        marginBottom: 14
      }} />
        <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8
      }}>
          <button onClick={() => {
          setShowAddExam(false);
          setNewExamName('');
        }} style={styles.btnSecondary}>Cancel</button>
          <button onClick={handleAddExam} disabled={addingExam || !newExamName.trim()} style={styles.btnPrimary}>
            {addingExam ? 'Adding…' : 'Add Exam'}
          </button>
        </div>
      </div>
    </div>;

  // ─── STUDENT LIST PANEL ───────────────────────────────────────────
  const StudentListPanel = () => <div style={{
    borderRadius: 12,
    overflow: 'hidden',
    border: '1px solid rgba(14,165,233,0.04)',
    background: '#fff'
  }}>
      <div style={{
      padding: 12,
      borderBottom: '1px solid #f1f5f9',
      background: 'linear-gradient(90deg,#f8fbff,#f1f5ff)',
      fontWeight: 800,
      color: '#0f172a',
      fontSize: isMobile ? 13 : 14
    }}>
        Students
      </div>
      <div style={{
      padding: 10
    }}>
        <input placeholder="Search students..." value={searchText} onChange={e => setSearchText(e.target.value)} style={{
        width: '100%',
        padding: 10,
        borderRadius: 10,
        border: '1px solid #eef2ff',
        marginBottom: 10,
        boxSizing: 'border-box',
        fontSize: isMobile ? 13 : 14
      }} />
        <div style={{
        maxHeight: isMobile ? 420 : 520,
        overflowY: 'auto'
      }}>
          {loading ? <div style={{
          padding: 12
        }}>Loading students…</div> : filteredStudents.length === 0 ? <div style={{
          padding: 12,
          color: '#9ca3af'
        }}>No students found.</div> : filteredStudents.map((s, idx) => <div key={s._id} onClick={() => openStudentReport(s)} style={{
          ...styles.studentCard,
          background: selectedStudent?._id === s._id ? '#f8fbff' : 'transparent'
        }} onMouseEnter={e => e.currentTarget.style.background = '#f8fbff'} onMouseLeave={e => e.currentTarget.style.background = selectedStudent?._id === s._id ? '#f8fbff' : 'transparent'}>
                <div style={{
            ...styles.studentAvatar,
            background: avatarColor(idx).bg,
            color: avatarColor(idx).color
          }}>
                  {(s.studentName || 'S').charAt(0)}
                </div>
                <div style={{
            flex: 1,
            minWidth: 0
          }}>
                  <div style={{
              fontWeight: 700,
              color: '#0f172a',
              fontSize: isMobile ? 13 : 14,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>{s.studentName}</div>
                  <div style={{
              fontSize: 11,
              color: '#6b7280',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>{s.rollNo}{s.fatherName ? ` · (${parentageLabel(s)})` : ''}</div>
                </div>
                <div style={{
            fontSize: 14,
            color: '#94a3b8',
            flexShrink: 0
          }}>›</div>
              </div>)}
        </div>
      </div>
    </div>;

  // ─── DETAIL PANEL ─────────────────────────────────────────────────
  const DetailPanel = () => {
    const {
      totalMarks,
      totalMax,
      pct
    } = computeMainTotals();
    const allSubs = [...mainSubjects, ...activeOptionalSubjects];
    const remainingOptional = optionalSubjectsAll.filter(s => !activeOptionalSubjects.includes(s));
    return <div style={{
      ...styles.panel,
      minHeight: 320
    }}>
        {!selectedStudent && <div style={{
        textAlign: 'center',
        color: '#9ca3af',
        paddingTop: 48
      }}>
            <div style={{
          fontSize: 36
        }}><FileEdit className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
            <div style={{
          fontWeight: 700,
          fontSize: 16
        }}>Select a student to view report card</div>
            <div style={{
          marginTop: 6
        }}>Click a student on the left to see marks across subjects.</div>
          </div>}

        {selectedStudent && <div>
            <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          flexWrap: 'wrap',
          gap: 8
        }}>
              <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 8 : 0
          }}>
                {isMobile && <button onClick={closeStudentReport} style={{
              ...styles.btnSecondary,
              marginRight: 8,
              padding: '6px 10px',
              fontSize: 18
            }}>
                    ‹
                  </button>}
                <div>
                  <div style={{
                fontSize: isMobile ? 15 : 18,
                fontWeight: 800
              }}>{selectedStudent.studentName}</div>
                  <div style={{
                fontSize: 11,
                color: '#6b7280'
              }}>{selectedStudent.rollNo}{selectedStudent.fatherName ? ` · (${parentageLabel(selectedStudent)})` : ''}</div>
                </div>
              </div>
              <div style={{
            display: 'flex',
            gap: 8,
            flexShrink: 0
          }}>
                <button onClick={() => setShowReportCard(s => !s)} style={styles.btnReport}>
                  {showReportCard ? 'Hide Report' : 'View Report'}
                </button>
                {!isMobile && <button onClick={closeStudentReport} style={{
              padding: '6px 10px',
              borderRadius: 8,
              border: '1px solid #e6eefc',
              background: '#fff',
              cursor: 'pointer',
              fontSize: 13
            }}>
                    Close
                  </button>}
              </div>
            </div>

            {reportLoading ? <div style={{
          padding: 20,
          textAlign: 'center',
          color: '#6b7280'
        }}>Loading report…</div> : showReportCard ?
        // ─── REPORT CARD VIEW ───
        <div style={styles.reportCard} id="printableReportCard">
                {/* Print-only isolation: when printing, show ONLY the report card */}
                <style>{`
                  @media print {
                    body * { visibility: hidden; }
                    #printableReportCard, #printableReportCard * { visibility: visible; }
                    #printableReportCard { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 24px; box-shadow: none; border: none; }
                    #printableReportCard .no-print { display: none !important; }
                  }
                `}</style>

                {/* Common Print option — always at the top */}
                <div className="no-print" style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 14
          }}>
                  <button onClick={() => window.print()} style={{
              ...styles.btnSecondary,
              background: '#0f172a',
              color: '#fff',
              border: 'none',
              fontWeight: 800
            }}>
                    <Printer className="inline w-[1em] h-[1em] align-[-0.125em]" /> Print Report Card
                  </button>
                </div>

                <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 6,
            flexWrap: 'wrap',
            gap: 8
          }}>
                  <div style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center'
            }}>
                    <div style={styles.reportLogo}>SC</div>
                    <div>
                      <div style={styles.schoolName}>Smart Campus</div>
                      <div style={{
                  fontSize: 12,
                  color: '#6b7280'
                }}>Report Card</div>
                    </div>
                  </div>
                  <div style={{
              textAlign: 'right',
              color: '#6b7280',
              fontSize: 12
            }}>
                    <div>Exam: {examTypes.find(e => e.name === selectedExam)?.name || selectedExam}</div>
                    <div>Academic Year: {new Date().getFullYear()}</div>
                  </div>
                </div>

                {/* Top row: Class, Section, Class Advisor */}
                <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
            padding: '10px 0',
            borderTop: '1px solid #f1f5f9',
            borderBottom: '1px solid #f1f5f9',
            marginBottom: 12,
            fontSize: isMobile ? 12 : 13,
            color: '#0f172a'
          }}>
                  <div>Class: <b>{classLabel || className || '—'}</b></div>
                  <div>Section: <b>{section || '—'}</b></div>
                  <div>Class Advisor: <b>{staffData?.name || selectedStudent.classAdvisor || '—'}</b></div>
                </div>

                {/* Side: Student Name & Roll Number */}
                <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 12,
            flexWrap: 'wrap',
            gap: 8
          }}>
                  <div>
                    <div style={{
                fontWeight: 800,
                fontSize: isMobile ? 14 : 16
              }}>{selectedStudent.studentName}</div>
                    <div style={{
                fontSize: 12,
                color: '#6b7280'
              }}>Roll No: {selectedStudent.rollNo}</div>
                    <div style={{
                fontSize: 12,
                color: '#6b7280'
              }}>{selectedStudent.fatherName ? `(${parentageLabel(selectedStudent)})` : ''}</div>
                  </div>
                </div>

                <div style={styles.tableHeader}>
                  <div style={styles.tableHeaderCell}>Subject</div>
                  <div style={styles.tableHeaderCell}>Marks</div>
                  <div style={styles.tableHeaderCell}>Max</div>
                  <div style={styles.tableHeaderCell}>Grade</div>
                </div>

                {allSubs.map((sub, idx) => {
            const entry = reportData.find(r => r.subject === sub && r.exam === selectedExam)?.entry || null;
            const inp = subjectInputs[sub] || {
              marks: entry?.marks ?? '',
              maxMarks: entry?.maxMarks ?? 100
            };
            const m = inp.marks === '' ? null : Number(inp.marks);
            const mm = Number(inp.maxMarks || 100);
            const g = calcGrade(m, mm);
            const gc = gradeColor(g);
            const isOptional = activeOptionalSubjects.includes(sub);
            return <div key={sub} style={{
              ...styles.tableRow,
              background: idx % 2 === 0 ? '#ffffff' : '#fbfdff'
            }}>
                      <div style={styles.tableCell}>{sub}{isOptional && <span style={{
                  marginLeft: 6,
                  fontSize: 10,
                  color: '#94a3b8',
                  fontWeight: 700
                }}>(optional)</span>}</div>
                      <div style={styles.tableCell}>{m === null ? '—' : m}</div>
                      <div style={styles.tableCell}>{mm}</div>
                      <div style={styles.tableCell}>
                        <span style={{
                  ...styles.gradeBadge,
                  background: gc.bg,
                  color: gc.color
                }}>{g}</span>
                      </div>
                    </div>;
          })}

                <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 12,
            flexWrap: 'wrap',
            gap: 8
          }}>
                  <div style={{
              fontWeight: 800,
              fontSize: isMobile ? 13 : 14
            }}>
                    Total: {totalMarks} / {totalMax}
                  </div>
                  <div style={{
              fontWeight: 800,
              fontSize: isMobile ? 13 : 14
            }}>
                    Percentage: {pct === null ? '—' : `${pct.toFixed(1)}%`}
                  </div>
                </div>
                <div style={{
            fontSize: 11,
            color: '#9ca3af',
            marginTop: 4
          }}>
                  * Total &amp; Percentage calculated from the {mainSubjects.length} main subjects only.
                </div>

                <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            marginTop: 24,
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? 16 : 0
          }}>
                  <div style={{
              textAlign: 'center',
              width: isMobile ? '100%' : 180
            }}>
                    <div style={{
                height: 48,
                borderBottom: '1px dashed #e6eefc',
                width: isMobile ? '100%' : 180
              }}></div>
                    <div style={{
                fontSize: 12,
                color: '#6b7280',
                marginTop: 4
              }}>Student Signature</div>
                  </div>
                  <div style={{
              textAlign: 'center',
              width: isMobile ? '100%' : 180
            }}>
                    <div style={{
                height: 48,
                borderBottom: '1px dashed #e6eefc',
                width: isMobile ? '100%' : 180
              }}></div>
                    <div style={{
                fontSize: 12,
                color: '#6b7280',
                marginTop: 4
              }}>Class Teacher</div>
                  </div>
                  <div style={{
              textAlign: 'center',
              width: isMobile ? '100%' : 180
            }}>
                    <div style={{
                height: 48,
                borderBottom: '1px dashed #e6eefc',
                width: isMobile ? '100%' : 180
              }}></div>
                    <div style={{
                fontSize: 12,
                color: '#6b7280',
                marginTop: 4
              }}>Principal</div>
                  </div>
                </div>

                <div className="no-print" style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 16
          }}>
                  <button onClick={() => setShowReportCard(false)} style={styles.btnSecondary}>Close</button>
                </div>
              </div> :
        // ─── MARKS ENTRY VIEW ───
        <div>
                <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(220px,1fr))',
            gap: 12
          }}>
                  {allSubs.length === 0 && <div style={{
              color: '#9ca3af'
            }}>No subjects configured for this class yet.</div>}
                  {allSubs.map(sub => {
              const entry = reportData.find(r => r.subject === sub && r.exam === selectedExam)?.entry || null;
              const inp = subjectInputs[sub] || {
                marks: entry?.marks ?? '',
                maxMarks: entry?.maxMarks ?? 100
              };
              const isOptional = activeOptionalSubjects.includes(sub);
              return <div key={sub} style={styles.card}>
                        <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                          <div style={{
                    fontWeight: 800,
                    color: '#0f172a',
                    fontSize: isMobile ? 13 : 14
                  }}>
                            {sub}{isOptional && <span style={{
                      marginLeft: 6,
                      fontSize: 10,
                      color: '#94a3b8',
                      fontWeight: 700
                    }}>optional</span>}
                          </div>
                          <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}>
                            <div style={{
                      fontSize: 11,
                      color: '#6b7280'
                    }}>{examTypes.find(e => e.name === selectedExam)?.name || selectedExam}</div>
                            {isOptional && <button onClick={() => removeOptionalSubject(sub)} title="Remove this optional subject" style={{
                      border: 'none',
                      background: '#fee2e2',
                      color: '#b91c1c',
                      borderRadius: 8,
                      width: 20,
                      height: 20,
                      cursor: 'pointer',
                      fontWeight: 800,
                      fontSize: 12,
                      lineHeight: '20px',
                      padding: 0
                    }}>
                                <X className="inline w-[1em] h-[1em] align-[-0.125em]" />
                              </button>}
                          </div>
                        </div>
                        <div style={{
                  marginTop: 10
                }}>
                          <div style={styles.inputsRow}>
                            <input type='number' value={inp.marks ?? ''} onChange={e => setSubjectInputs(p => ({
                      ...p,
                      [sub]: {
                        ...p[sub],
                        marks: e.target.value
                      }
                    }))} placeholder='Marks' style={styles.input} />
                            <div style={{
                      fontSize: 18,
                      fontWeight: 700
                    }}>/</div>
                            <input type='number' value={inp.maxMarks ?? 100} onChange={e => setSubjectInputs(p => ({
                      ...p,
                      [sub]: {
                        ...p[sub],
                        maxMarks: e.target.value
                      }
                    }))} placeholder='Max' style={{
                      ...styles.input,
                      width: isMobile ? 60 : 80
                    }} />
                          </div>
                          <div style={styles.gradeRow}>
                            <div style={{
                      fontWeight: 700,
                      color: '#0f172a',
                      fontSize: isMobile ? 13 : 14
                    }}>Grade:</div>
                            <div style={{
                      padding: '5px 10px',
                      borderRadius: 8,
                      background: '#f1f5f9',
                      color: '#0f172a',
                      fontWeight: 800,
                      fontSize: isMobile ? 13 : 14
                    }}>
                              {calcGrade(inp.marks === '' ? null : Number(inp.marks), Number(inp.maxMarks || 100))}
                            </div>
                          </div>
                        </div>
                      </div>;
            })}
                </div>

                {/* Add optional subject row */}
                {remainingOptional.length > 0 && <div style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            marginTop: 14,
            flexWrap: 'wrap'
          }}>
                    <select value={addOptionalChoice} onChange={e => setAddOptionalChoice(e.target.value)} style={{
              ...styles.select,
              minWidth: 180
            }}>
                      <option value="">+ Add optional subject…</option>
                      {remainingOptional.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                    </select>
                    <button onClick={addOptionalSubject} disabled={!addOptionalChoice} style={styles.btnSecondary}>Add Subject</button>
                  </div>}

                <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 16,
            flexWrap: 'wrap',
            gap: 8
          }}>
                  <div style={{
              fontSize: 13,
              color: '#6b7280'
            }}>
                    Total: <b style={{
                color: '#0f172a'
              }}>{totalMarks}/{totalMax}</b> &nbsp; Percentage: <b style={{
                color: '#0f172a'
              }}>{pct === null ? '—' : `${pct.toFixed(1)}%`}</b>
                  </div>
                  <button onClick={saveAllForSelectedStudent} disabled={savingSingleId === selectedStudent?._id || allSubs.length === 0} style={{
              ...styles.btnPrimary,
              ...styles.saveAllBtn,
              marginTop: 0
            }}>
                    {savingSingleId === selectedStudent?._id ? 'Saving…' : 'Save All Subjects'}
                  </button>
                </div>
              </div>}
          </div>}
      </div>;
  };

  // ─── CLASS REPORT MODAL (all students in this class/section) ─────────────────
  const ClassReportModal = () => <div style={styles.modalOverlay} onClick={closeClassReport}>
      <div id="printableClassReport" onClick={e => e.stopPropagation()} style={{
      background: '#fff',
      borderRadius: 14,
      padding: isMobile ? 14 : 24,
      width: isMobile ? '100%' : '95%',
      maxWidth: 1100,
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 20px 60px rgba(2,6,23,0.25)'
    }}>
        {/* Print-only isolation: when printing, show ONLY this class report */}
        <style>{`
          @media print {
            body * { visibility: hidden; }
            #printableClassReport, #printableClassReport * { visibility: visible; }
            #printableClassReport { position: absolute; left: 0; top: 0; width: 100%; max-height: none; overflow: visible; margin: 0; padding: 24px; box-shadow: none; }
            #printableClassReport .no-print { display: none !important; }
          }
        `}</style>

        {/* Common Print option — always at the top */}
        <div className="no-print" style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
        marginBottom: 14
      }}>
          <button onClick={() => window.print()} style={{
          ...styles.btnSecondary,
          background: '#0f172a',
          color: '#fff',
          border: 'none',
          fontWeight: 800
        }}>
            <Printer className="inline w-[1em] h-[1em] align-[-0.125em]" /> Print Class Report
          </button>
          <button onClick={closeClassReport} style={styles.btnSecondary}>Close</button>
        </div>

        <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 6,
        flexWrap: 'wrap',
        gap: 8
      }}>
          <div style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center'
        }}>
            <div style={styles.reportLogo}>SC</div>
            <div>
              <div style={styles.schoolName}>Smart Campus</div>
              <div style={{
              fontSize: 12,
              color: '#6b7280'
            }}>Class Report Card</div>
            </div>
          </div>
          <div style={{
          textAlign: 'right',
          color: '#6b7280',
          fontSize: 12
        }}>
            <div>Exam: {examTypes.find(e => e.name === selectedExam)?.name || selectedExam}</div>
            <div>Academic Year: {new Date().getFullYear()}</div>
          </div>
        </div>

        {/* Class, Section, Class Advisor */}
        <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 8,
        padding: '10px 0',
        borderTop: '1px solid #f1f5f9',
        borderBottom: '1px solid #f1f5f9',
        marginBottom: 14,
        fontSize: isMobile ? 12 : 13,
        color: '#0f172a'
      }}>
          <div>Class: <b>{classLabel || className || '—'}</b></div>
          <div>Section: <b>{section || '—'}</b></div>
          <div>Class Advisor: <b>{staffData?.name || '—'}</b></div>
        </div>

        {classReportLoading ? <div style={{
        padding: 20,
        textAlign: 'center',
        color: '#6b7280'
      }}>Loading class report…</div> : students.length === 0 ? <div style={{
        padding: 20,
        textAlign: 'center',
        color: '#9ca3af'
      }}>No students in this class.</div> : <div style={{
        overflowX: 'auto'
      }}>
            <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: isMobile ? 11 : 13
        }}>
              <thead>
                <tr>
                  <th style={styles.classTableTh}>#</th>
                  <th style={{
                ...styles.classTableTh,
                textAlign: 'left'
              }}>Name</th>
                  <th style={styles.classTableTh}>Roll No</th>
                  {mainSubjects.map(sub => <th key={sub} style={styles.classTableTh}>{sub}</th>)}
                  <th style={styles.classTableTh}>Total</th>
                  <th style={styles.classTableTh}>%</th>
                  <th style={styles.classTableTh}>Signature</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s, idx) => {
              const subData = classMarksData[String(s._id)] || {};
              let totalMarks = 0,
                totalMax = 0,
                anyEntered = false;
              mainSubjects.forEach(sub => {
                const e = subData[sub];
                const mm = Number(e?.maxMarks ?? 100);
                totalMax += isNaN(mm) ? 0 : mm;
                if (e && e.marks !== null && e.marks !== undefined) {
                  anyEntered = true;
                  totalMarks += Number(e.marks);
                }
              });
              const pct = anyEntered && totalMax > 0 ? totalMarks / totalMax * 100 : null;
              return <tr key={s._id}>
                      <td style={styles.classTableTd}>{idx + 1}</td>
                      <td style={{
                  ...styles.classTableTd,
                  textAlign: 'left',
                  fontWeight: 700
                }}>{s.studentName}</td>
                      <td style={styles.classTableTd}>{s.rollNo}</td>
                      {mainSubjects.map(sub => {
                  const e = subData[sub];
                  return <td key={sub} style={styles.classTableTd}>
                            {e && e.marks !== null && e.marks !== undefined ? e.marks : '—'}
                          </td>;
                })}
                      <td style={{
                  ...styles.classTableTd,
                  fontWeight: 800
                }}>{totalMarks}/{totalMax}</td>
                      <td style={{
                  ...styles.classTableTd,
                  fontWeight: 800
                }}>{pct === null ? '—' : `${pct.toFixed(1)}%`}</td>
                      <td style={{
                  ...styles.classTableTd,
                  minWidth: 90
                }}></td>
                    </tr>;
            })}
              </tbody>
            </table>
            <div style={{
          fontSize: 11,
          color: '#9ca3af',
          marginTop: 8
        }}>
              * Total &amp; Percentage calculated from the {mainSubjects.length} main subjects only.
            </div>
          </div>}
      </div>
    </div>;
  return <div style={styles.container}>
      {/* ─── HEADER ─── */}
      <div style={styles.header}>
        <div style={{
        flexShrink: 0
      }}>
          <div style={styles.title}>Marksheet — {className}</div>
          <div style={styles.subtitle}>{students.length} students{department ? ` · ${department.charAt(0).toUpperCase() + department.slice(1)} group` : ''}</div>
        </div>

        {/* Exam tabs — scrollable on mobile — plus Add Exam button */}
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flex: isMobile ? '0 0 auto' : '1 1 240px',
        minWidth: 0,
        maxWidth: isMobile ? '100%' : 'none'
      }}>
          <div style={styles.tabsContainer}>
            {examTypes.map(examOption => {
            const isActive = selectedExam === examOption.name;
            return <button key={examOption._id} onClick={() => setSelectedExam(examOption.name)} style={{
              ...styles.tabButton,
              ...(isActive ? styles.tabButtonActive : {})
            }}>
                  {examOption.name}
                </button>;
          })}
          </div>
          <button onClick={() => setShowAddExam(true)} style={styles.addExamBtn}>+ Exam</button>
        </div>

        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0
      }}>
          <button onClick={loadClassReport} style={{
          ...styles.btnSecondary,
          fontWeight: 800
        }}><BarChart3 className="inline w-[1em] h-[1em] align-[-0.125em]" /> Class Report</button>
          <button onClick={onClose} style={styles.btnSecondary}>Close</button>
        </div>
      </div>

      {/* ─── BODY ─── */}
      {isMobile ?
    // Mobile: show either list OR detail, not side-by-side
    mobileView === 'list' ? <StudentListPanel /> : <DetailPanel /> :
    // Desktop: side-by-side grid
    <div style={{
      display: 'grid',
      gridTemplateColumns: '320px 1fr',
      gap: 16,
      minHeight: 320
    }}>
          <StudentListPanel />
          <DetailPanel />
        </div>}

      {showAddExam && <AddExamModal />}
      {showClassReport && <ClassReportModal />}
    </div>;
}